import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Space,
  Tabs,
  message,
  Spin,
  Empty,
  Modal,
  Typography,
  Descriptions,
  Tag,
  Table,
  Image,
  Progress,
  Statistic,
  Row,
  Col,
  Form,
  Select,
  Input,
} from "antd";
import {
  ArrowLeftOutlined,
  ImportOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import { StoreDisplayGroup } from "../../../types/storeDisplayGroup";
import {
  StoreDisplayGroupPlanogram,
  StoreDisplayGroupPlanogramFilters,
  PlanogramPaginationParams,
} from "../../../types/storeDisplayGroupPlanogram";
import StoreDisplayGroupService from "../../../services/storeDisplayGroupService";
import StoreDisplayGroupPlanogramService from "../../../services/storeDisplayGroupPlanogramService";
import NetworkError from "../../../components/common/NetworkError";
import EmptyState from "../../../components/common/EmptyState";
import FilterPanel from "../../../components/common/FilterPanel";
import { handleApiResponse } from "../../../utils/responseTransformer";
import {
  isRetryableError,
  sanitizeErrorForDisplay,
} from "../../../utils/errorHandler";
import dayjs from "dayjs";

const { Title } = Typography;

const StoreDisplayGroupPlanogramView: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState<boolean>(true);
  const [planogramLoading, setPlanogramLoading] = useState<boolean>(false);
  const [storeDisplayGroup, setStoreDisplayGroup] =
    useState<StoreDisplayGroup | null>(null);
  const [planogramData, setPlanogramData] = useState<
    StoreDisplayGroupPlanogram[]
  >([]);
  const [total, setTotal] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  // 分页和筛选状态
  const [pagination, setPagination] = useState<PlanogramPaginationParams>({
    page: 1,
    pageSize: 10,
    sortBy: "gmtModified",
    sortOrder: "desc",
  });

  const [filters, setFilters] = useState<StoreDisplayGroupPlanogramFilters>({});
  const [activeTab, setActiveTab] = useState<string>("list");

  // 表格选择状态
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [manualTriggerLoading, setManualTriggerLoading] =
    useState<boolean>(false);

  // 加载门店DG详情
  const loadStoreDisplayGroupDetail = useCallback(async () => {
    if (!code) return;

    setLoading(true);
    setError(null);

    try {
      const response =
        await StoreDisplayGroupService.getStoreDisplayGroupByCode(code);
      const displayGroup = handleApiResponse(
        response,
        "loadStoreDisplayGroupDetail"
      );
      setStoreDisplayGroup(displayGroup as StoreDisplayGroup);
    } catch (error) {
      const errorMessage = sanitizeErrorForDisplay(error);
      setError(errorMessage);
      setStoreDisplayGroup(null);
    } finally {
      setLoading(false);
    }
  }, [code]);

  // 加载Planogram数据
  const loadPlanogramData = useCallback(
    async (showLoading = true) => {
      if (!storeDisplayGroup) {
        console.log(
          "StoreDisplayGroup not loaded yet, skipping planogram data load"
        );
        return;
      }

      if (showLoading) setPlanogramLoading(true);

      try {
        // 使用门店DG的storeCode和shopGroupCode查询planogram数据
        const queryParams = {
          storeCode: storeDisplayGroup.storeCode,
          displayGroupCode: storeDisplayGroup.displayGroupCode,
          ...filters,
        };

        console.log("Loading planogram data with params:", queryParams);

        const response =
          await StoreDisplayGroupPlanogramService.getPlanogramList(
            queryParams,
            pagination
          );
        console.log("Planogram data response:", response);

        setPlanogramData(response.data.tableData);
        setTotal(response.data.paging.totalCount);
      } catch (error) {
        message.error(t("storeDisplayGroupPlanogram.fetchFailed"));
        console.error("Failed to fetch planogram data:", error);
        setPlanogramData([]);
        setTotal(0);
      } finally {
        if (showLoading) setPlanogramLoading(false);
      }
    },
    [storeDisplayGroup, filters, pagination, t]
  );

  // 初始化数据加载
  useEffect(() => {
    loadStoreDisplayGroupDetail();
  }, [loadStoreDisplayGroupDetail]);

  useEffect(() => {
    if (storeDisplayGroup) {
      loadPlanogramData();
    }
  }, [loadPlanogramData]);

  // 处理返回按钮
  const handleBack = useCallback(() => {
    navigate("/store-display-groups");
  }, [navigate]);

  // 处理筛选变化
  const handleFiltersChange = useCallback((values: any) => {
    const newFilters: StoreDisplayGroupPlanogramFilters = {
      status: values.status,
      source: values.source,
      keyword: values.keyword,
    };
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  // 处理重置筛选
  const handleReset = useCallback(() => {
    setFilters({});
    setPagination({
      page: 1,
      pageSize: 20,
      sortBy: "gmtModified",
      sortOrder: "desc",
    });
    return {};
  }, []);

  // 处理表格变化
  const handleTableChange = useCallback(
    (paginationConfig: any, _filters: any, sorter: any) => {
      const newPagination: PlanogramPaginationParams = {
        page: paginationConfig.current,
        pageSize: paginationConfig.pageSize,
      };

      if (sorter.field && sorter.order) {
        newPagination.sortBy = sorter.field;
        newPagination.sortOrder = sorter.order === "ascend" ? "asc" : "desc";
      }

      setPagination(newPagination);
    },
    []
  );

  // 处理表格行选择
  const handleRowSelectionChange = useCallback(
    (selectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(selectedRowKeys);
    },
    []
  );

  // 处理查看详情
  const handleViewDetail = useCallback(
    (record: StoreDisplayGroupPlanogram) => {
      navigate(`/store-display-groups/${code}/planogram/${record.code}/view`);
    },
    [navigate, code]
  );

  // 处理重试
  const handleRetry = useCallback(() => {
    loadStoreDisplayGroupDetail();
  }, [loadStoreDisplayGroupDetail]);

  // 手动触发执行
  const handleManualTriggerExec = useCallback(async () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t("storeDisplayGroupPlanogram.selectActiveItems"));
      return;
    }

    // 获取选中的记录
    const selectedRecords = planogramData.filter(
      (record) =>
        selectedRowKeys.includes(record.id) && record.status === "ACTIVE"
    );

    if (selectedRecords.length === 0) {
      message.warning(t("storeDisplayGroupPlanogram.noActiveItemsSelected"));
      return;
    }
    if (selectedRecords.length > 1) {
      message.warning(t("storeDisplayGroupPlanogram.onlyOneActiveItemAllowed"));
      return;
    }

    // 获取选中记录的code
    const planogramId = selectedRecords[0].id;

    // 添加确认弹框
    Modal.confirm({
      title: t("storeDisplayGroupPlanogram.manualTriggerConfirmTitle"),
      content: t("storeDisplayGroupPlanogram.manualTriggerConfirmContent"),
      okText: t("common.confirm"),
      cancelText: t("common.cancel"),
      onOk: async () => {
        try {
          setManualTriggerLoading(true);
          const response =
            await StoreDisplayGroupPlanogramService.manualTriggerExec(
              planogramId
            );

          if (response.success) {
            message.success(
              t("storeDisplayGroupPlanogram.manualTriggerSuccess")
            );
            // 重新加载数据
            loadPlanogramData();
            // 清除选择
            setSelectedRowKeys([]);
          } else {
            message.error(
              response.errorMessage ||
                t("storeDisplayGroupPlanogram.manualTriggerFailed")
            );
          }
        } catch (error) {
          message.error(t("storeDisplayGroupPlanogram.manualTriggerFailed"));
          console.error("Manual trigger exec failed:", error);
        } finally {
          setManualTriggerLoading(false);
        }
      },
      onCancel() {
        // 用户取消操作，不做任何事情
      },
    });
  }, [selectedRowKeys, planogramData, t, loadPlanogramData]);

  // 格式化时间
  const formatDateTime = (timestamp: string): string => {
    return timestamp ? dayjs(timestamp).format("YYYY-MM-DD HH:mm:ss") : "-";
  };

  // Status mapping function
  const getStatusKey = (status: string): string => {
    const statusMap: Record<string, string> = {
      草稿: "Draft",
      审核中: "PENDING",
      审核拒绝: "REJECTED",
      生效中: "ACTIVE",
      已归档: "ARCHIVED",
    };
    return statusMap[status] || status;
  };

  const renderHasActive = (status) => {
    if (status === true) {
      return <Tag color="green">true</Tag>;
    }
    return <Tag color="red">false</Tag>;
  };

  // 定义表格列
  const columns: ColumnsType<StoreDisplayGroupPlanogram> = [
    {
      title: t("storeDisplayGroupPlanogram.code"),
      dataIndex: "code",
      key: "code",
      // sorter: true,
      width: 220,
      // sortOrder: pagination.sortBy === 'code' ? (pagination.sortOrder === 'asc' ? 'ascend' : 'descend') : null,
    },
    {
      title: t("storeDisplayGroupPlanogram.source"),
      dataIndex: "source",
      key: "source",
      render: (source: string) => (
        <Tag color={source === "benchmark" ? "blue" : "green"}>
          {t(`storeDisplayGroupPlanogram.sourceType.${source}`)}
        </Tag>
      ),
      width: 140,
    },
    {
      title: t("storeDisplayGroupPlanogram.itemCount"),
      dataIndex: "itemCount",
      key: "itemCount",
      // sorter: true,
      // sortOrder: pagination.sortBy === 'itemCount' ? (pagination.sortOrder === 'asc' ? 'ascend' : 'descend') : null,
      width: 100,
      align: "center",
    },
    {
      title: t("storeDisplayGroupPlanogram.status"),
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        const statusColors = {
          草稿: "default",
          审核中: "processing",
          审核拒绝: "error",
          生效中: "success",
          已归档: "warning",
        };
        return (
          <Tag
            color={
              statusColors[status as keyof typeof statusColors] || "default"
            }
          >
            {t(
              `storeDisplayGroupPlanogram.statusType.${getStatusKey(status)}`
            ) || status}
          </Tag>
        );
      },
      width: 100,
      align: "center",
    },
    {
      title: t("storeDisplayGroupPlanogram.imageUrl"),
      dataIndex: "imageUrl",
      key: "imageUrl",
      render: (imageUrl: string) =>
        imageUrl ? (
          <Image
            src={imageUrl + "?x-oss-process=image/resize,w_200,h_150"}
            width={60}
            height={40}
            style={{ objectFit: "cover", borderRadius: "4px" }}
            fallback={imageUrl}
            preview={
              imageUrl && {
                src: imageUrl,
              }
            }
          />
        ) : (
          <span style={{ color: "#999" }}>-</span>
        ),
      width: 100,
      align: "center",
    },
    {
      title: t("storeDisplayGroupPlanogram.manualImageUrl"),
      dataIndex: "manualImageUrl",
      key: "manualImageUrl",
      render: (manualImageUrl: string) =>
        manualImageUrl ? (
          <Image
            src={manualImageUrl + "?x-oss-process=image/resize,w_200,h_150"}
            width={60}
            height={40}
            style={{ objectFit: "cover", borderRadius: "4px" }}
            fallback={manualImageUrl}
            preview={
              manualImageUrl && {
                src: manualImageUrl,
              }
            }
          />
        ) : (
          <span style={{ color: "#999" }}>-</span>
        ),
      width: 100,
      align: "center",
    },
    {
      title: t("storeDisplayGroupPlanogram.manualImageByInventoryUrl"),
      dataIndex: "manualImageByInventoryUrl",
      key: "manualImageByInventoryUrl",
      render: (manualImageByInventoryUrl: string) =>
        manualImageByInventoryUrl ? (
          <Image
            src={
              manualImageByInventoryUrl +
              "?x-oss-process=image/resize,w_200,h_150"
            }
            width={60}
            height={40}
            style={{ objectFit: "cover", borderRadius: "4px" }}
            fallback={manualImageByInventoryUrl}
            preview={
              manualImageByInventoryUrl && {
                src: manualImageByInventoryUrl,
              }
            }
          />
        ) : (
          <span style={{ color: "#999" }}>-</span>
        ),
      width: 120,
      align: "center",
    },
    {
      title: t("storeDisplayGroupPlanogram.hasActiveTasks"),
      dataIndex: "hasActiveTasks",
      key: "hasActiveTasks",
      render: renderHasActive,
      width: 150,
    },
    {
      title: t("storeDisplayGroupPlanogram.taskGeneratedTime"),
      dataIndex: "taskGeneratedTime",
      key: "taskGeneratedTime",
      render: formatDateTime,
      width: 150,
    },
    {
      title: t("storeDisplayGroupPlanogram.createTime"),
      dataIndex: "gmtCreate",
      key: "gmtCreate",
      render: formatDateTime,
      width: 150,
    },
    {
      title: t("storeDisplayGroupPlanogram.updateTime"),
      dataIndex: "gmtModified",
      key: "gmtModified",
      render: formatDateTime,
      width: 150,
    },
    {
      title: t("common.actions"),
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
            title={t("common.view")}
          />
        </Space>
      ),
    },
  ];

  // 表格行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: handleRowSelectionChange,
    getCheckboxProps: (record: StoreDisplayGroupPlanogram) => ({
      disabled:
        record.status !== "ACTIVE" ||
        record.taskGeneratedTime == null ||
        record.hasActiveTasks, // 只允许选择状态为"生效中"的记录
    }),
  };

  // 显示加载状态
  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  // 显示错误状态
  if (error && isRetryableError({ message: error })) {
    return (
      <div className="page-container">
        <NetworkError onRetry={handleRetry} message={error} />
      </div>
    );
  }

  // 显示门店DG不存在
  if (!storeDisplayGroup) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <h2>{t("storeDisplayGroup.notFoundOrDeleted")}</h2>
        <Button type="primary" onClick={handleBack}>
          {t("common.back")}
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* 页面头部 */}
      <Row
        justify="space-between"
        align="middle"
        style={{ marginBottom: "24px" }}
      >
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
              {t("common.back")}
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              {t("storeDisplayGroupPlanogram.pageTitle")}
            </Title>
          </Space>
        </Col>
      </Row>

      {/* 门店DG基本信息 */}
      <Card
        title={t("storeDisplayGroup.basicInfo.title")}
        style={{ marginBottom: "16px" }}
      >
        <Descriptions column={2} bordered>
          <Descriptions.Item label={t("storeDisplayGroup.code")}>
            {storeDisplayGroup.code}
          </Descriptions.Item>
          <Descriptions.Item label={t("storeDisplayGroup.name")}>
            {storeDisplayGroup.name}
          </Descriptions.Item>
          <Descriptions.Item label={t("storeDisplayGroup.shop")}>
            {storeDisplayGroup.storeCode} - {storeDisplayGroup.storeName}
          </Descriptions.Item>
          <Descriptions.Item label={t("storeDisplayGroup.shopGroup")}>
            {storeDisplayGroup.shopGroupCode} -{" "}
            {storeDisplayGroup.shopGroupName}
          </Descriptions.Item>
          <Descriptions.Item
            label={t("hqDisplayGroup.filter.hasWhitelistItems")}
          >
            {storeDisplayGroup.whiteListItems != null &&
            storeDisplayGroup.whiteListItems.length > 0
              ? "Yes"
              : "No"}
          </Descriptions.Item>
          <Descriptions.Item
            label={t("hqDisplayGroup.filter.hasBlacklistItems")}
          >
            {storeDisplayGroup.blackListItems != null &&
            storeDisplayGroup.blackListItems.length > 0
              ? "Yes"
              : "No"}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Planogram管理 */}
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane
            key="list"
            tab={t("storeDisplayGroupPlanogram.listTab")}
          >
            {/* 筛选面板 */}
            <FilterPanel
              onFilter={handleFiltersChange}
              onReset={handleReset}
              // initialValues={filters}
            >
              <Col span={6}>
                <Form.Item
                  name="status"
                  label={t("storeDisplayGroupPlanogram.filter.status")}
                >
                  <Select
                    placeholder={t(
                      "storeDisplayGroupPlanogram.filter.statusPlaceholder"
                    )}
                    allowClear
                  >
                    <Select.Option value="INIT">
                      {t("storeDisplayGroupPlanogram.statusType.Draft")}
                    </Select.Option>
                    <Select.Option value="ACTIVE">
                      {t("storeDisplayGroupPlanogram.statusType.ACTIVE")}
                    </Select.Option>
                    <Select.Option value="ARCHIVED">
                      {t("storeDisplayGroupPlanogram.statusType.ARCHIVED")}
                    </Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="source"
                  label={t("storeDisplayGroupPlanogram.filter.source")}
                >
                  <Select
                    placeholder={t(
                      "storeDisplayGroupPlanogram.filter.sourcePlaceholder"
                    )}
                    allowClear
                  >
                    <Select.Option value="benchmark">
                      {t("storeDisplayGroupPlanogram.sourceType.benchmark")}
                    </Select.Option>
                    <Select.Option value="algorithm">
                      {t("storeDisplayGroupPlanogram.sourceType.algorithm")}
                    </Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </FilterPanel>

            {/* 批量操作按钮 */}
            <div style={{ marginBottom: "16px" }}>
              <Button
                type="primary"
                onClick={handleManualTriggerExec}
                loading={manualTriggerLoading}
                disabled={selectedRowKeys.length === 0}
              >
                {t("storeDisplayGroupPlanogram.manualTriggerExec")}
              </Button>
              <span style={{ marginLeft: "12px", color: "#999" }}>
                {t("storeDisplayGroupPlanogram.selectedCount", {
                  count: selectedRowKeys.length,
                })}
              </span>
            </div>

            {/* 数据表格 */}
            <Table
              columns={columns}
              dataSource={planogramData}
              rowKey="id"
              rowSelection={rowSelection}
              loading={planogramLoading}
              pagination={{
                current: pagination.page,
                pageSize: pagination.pageSize,
                total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  t("storeDisplayGroupPlanogram.pagination.total", {
                    start: range[0],
                    end: range[1],
                    total,
                  }),
                pageSizeOptions: ["10", "20"],
              }}
              onChange={handleTableChange}
              scroll={{ x: 1200, y: 500 }}
              locale={{
                emptyText:
                  planogramData.length === 0 && !planogramLoading ? (
                    <EmptyState
                      type={
                        Object.keys(filters).some(
                          (key) =>
                            filters[
                              key as keyof StoreDisplayGroupPlanogramFilters
                            ]
                        )
                          ? "filter"
                          : "default"
                      }
                      onAction={
                        Object.keys(filters).some(
                          (key) =>
                            filters[
                              key as keyof StoreDisplayGroupPlanogramFilters
                            ]
                        )
                          ? handleReset
                          : undefined
                      }
                      actionText={
                        Object.keys(filters).some(
                          (key) =>
                            filters[
                              key as keyof StoreDisplayGroupPlanogramFilters
                            ]
                        )
                          ? t("common.reset")
                          : undefined
                      }
                    />
                  ) : undefined,
              }}
            />
          </Tabs.TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default StoreDisplayGroupPlanogramView;
